import React, { PropsWithChildren, useEffect, useState } from 'react';
import { NavigationProvider } from './context';
import { INavigation, SectionProps } from './types';

const DEFAULT_ACTIVE_SECTION_ID = '';

/**
 * NavigationContainer
 *
 * @param {INavigation} props - The props for the navigation container component.
 * @param {Array<SectionProps>} props.sections - An array of sections. The type of section is ISection
 * @param {ReactNode} props.children - The children nodes to be rendered.
 *
 * @returns {JSX.Element} The navigation container component.
 *
 * https://github.com/ClearTax/mp-frontend/wiki/Navigation
 */
export const NavigationContainer: React.FC<PropsWithChildren<INavigation>> = ({
  sections,
  children,
  allowEmptyContent = false,
  disableIntersectionObserver = false,
}) => {
  const [activeSectionId, setActiveSectionId] = useState(
    DEFAULT_ACTIVE_SECTION_ID
  );
  const [sectionsDefined, setSectionsDefined] = useState(false);
  const [justScrolled, setJustScrolled] = useState(false);
  const [sectionsWithData, setSectionsWithData] = useState<Array<SectionProps>>(
    []
  );

  /**
   * Callback function for the Intersection Observer API that detects
   * when sections of a page come into view. When a section comes into view,
   * its ID is added to an array of currently visible sections.
   * If there is at least one visible section and the user has not just scrolled,
   * the first visible section is set as the active section.
   */
  const handleObserver: IntersectionObserverCallback = (entries) => {
    const inViewSections = entries
      .filter((entry) => entry.isIntersecting)
      .map((entry) => entry.target.id);
    if (!justScrolled && inViewSections.length) {
      setActiveSectionId(inViewSections[0] as string);
    }
  };

  //useEffect for Setting the first section as default
  // active once sections have been defined
  useEffect(() => {
    if (sectionsWithData[0]) setActiveSectionId(sectionsWithData[0].props.id);
  }, [sectionsDefined]);
  /**
   * useEffect hook for IntersectionObserver.
   * Observes all sections and sets the active section when it crosses the threshold.
   */
  useEffect(() => {
    const tempSections = sections.filter((section) => {
      if (allowEmptyContent) return true;
      if (section.ref.current?.children.length && !section.props.excludeFromNav)
        return true;
      return false;
    });

    setSectionsWithData(tempSections);
    if (!sectionsDefined) setSectionsDefined(true);

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.5,
    });

    if (!disableIntersectionObserver) {
      tempSections.forEach((section) => {
        if (section.ref.current) observer.observe(section.ref.current);
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [justScrolled]);

  /**
   * useEffect hook to reset the `justScrolled` after 1 second.
   */
  useEffect(() => {
    if (justScrolled) {
      setTimeout(() => {
        setJustScrolled(false);
      }, 1000);
    }
  }, [justScrolled]);

  return (
    <NavigationProvider
      value={{
        sections: sectionsWithData,
        activeSection: activeSectionId,
        setJustScrolledCB: setJustScrolled,
        setActiveSectionCB: setActiveSectionId,
      }}
    >
      {children}
    </NavigationProvider>
  );
};
